import { api } from './api';

/**
 * Interview simulations for the Matchmaker Chat page. Unlike
 * `matchmakerChatApi`, these calls go out as the admin (no `x-login-as`):
 * the admin owns the run and receives its socket progress events.
 */

export type SimulationGoal = 'first_card' | 'half' | 'none';
export type SimulationStatus = 'running' | 'done' | 'stopped' | 'failed';
export type SimulationStopReason =
  | 'goal'
  | 'cap'
  | 'stopped'
  | 'failed_turn'
  | 'persona_repeat'
  | 'error';

export interface SimulationMetrics {
  userTurns: number;
  hongTurns: number;
  avgUserChars: number;
  avgHongChars: number;
  truthFields: number;
  recalled: number;
  recallRate: number | null;
  capturedBeyondFacts: number;
  declined: number;
  turnsToFirstCard: number | null;
  cards: number;
  turnsTo50: number | null;
  finalPercentage: number;
  fieldWrites: number;
  guidanceCalls: number;
  repeatedGuidanceGroups: string[];
  failedTurns: number;
}

export interface JudgeVerdict {
  naturalness: number;
  noRepetition: number;
  listens: number;
  pacing: number;
  languageConsistency: number;
  overall: number;
  issues: { turn: number; note: string }[];
}

export interface Simulation {
  id: string;
  sourceUserId: string;
  cloneUserId: string;
  userConversationId: string;
  createdBy: string;
  lang: 'en' | 'cn';
  goal: SimulationGoal;
  maxTurns: number;
  promptOverrides: Record<string, number> | null;
  status: SimulationStatus;
  stopReason: SimulationStopReason | null;
  turns: number;
  progress: { turn: number; percentage: number }[];
  metrics: SimulationMetrics | null;
  judge: JudgeVerdict | null;
  error: string | null;
  createdAt: string;
  finishedAt: string | null;
}

export interface StartSimulationDto {
  sourceUserId: string;
  goal: SimulationGoal;
  maxTurns?: number;
  lang?: 'en' | 'cn';
  promptOverrides?: Record<string, number>;
}

/** Socket ADMIN_LOG payload for a simulation (one per turn, plus the end). */
export interface SimulationEvent {
  type: 'simulation';
  simulationId: string;
  status: SimulationStatus;
  turn: number;
  maxTurns: number;
  percentage?: number;
  stopReason?: SimulationStopReason | null;
}

export const simulationApi = api.injectEndpoints({
  endpoints: (builder) => ({
    startSimulation: builder.mutation<Simulation, StartSimulationDto>({
      query: (body) => ({
        url: '/matchmaker-simulations',
        method: 'POST',
        body,
      }),
    }),
    listSimulations: builder.query<
      Simulation[],
      { sourceUserId?: string; cloneUserId?: string }
    >({
      query: (params) => ({
        url: '/matchmaker-simulations',
        method: 'GET',
        params,
      }),
    }),
    getSimulation: builder.query<Simulation, string>({
      query: (id) => ({ url: `/matchmaker-simulations/${id}`, method: 'GET' }),
    }),
    stopSimulation: builder.mutation<Simulation, string>({
      query: (id) => ({
        url: `/matchmaker-simulations/${id}/stop`,
        method: 'POST',
      }),
    }),
    deleteSimulation: builder.mutation<{ deleted: boolean }, string>({
      query: (id) => ({
        url: `/matchmaker-simulations/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useStartSimulationMutation,
  useLazyListSimulationsQuery,
  useLazyGetSimulationQuery,
  useStopSimulationMutation,
  useDeleteSimulationMutation,
} = simulationApi;
